import { useEffect, useState } from "react";

import OptionalFields, { ToggleButton } from "./optionalFieds";
import FloatingLabelDate from "./floatingLabelDate";
import FloatingLabelInput from "./floatingLabelInput";
import FloatingLabelSelect from "./floatingLabelSelect";
import FloatingLabelTextArea from "./floatingLabelTextArea";
import ModalShell from "./modalShell";
import { formatPhoneNumber, formatDollarWithComma, formatDate, earliestStartDate } from "../features/utils";
import { RIZE_EMPLOYEE_TITLES, isOverrideOnlyTitle, isRizeForRealtorFlow } from "../config/flows";
import { showValidationError } from "../admin/components/onboarding/apiHelper";


const initialData = {
    tentativeStartDate: formatDate(new Date()),
    firstName: '',
    lastName: '',
    title: 'Area Manager',
    email: '',
    cellNumber: '',
    employementType: 'Salary, Full Time (Recoverable)',
    compensationRate: '',
    isOriginatingLoans: false,
    isShowTransactions: false,
    overrideBps: '',
    transactions: [
        {   label: 'Self Generated Transactions (bps)', 
            value: '', isBoxToggled: true, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: true, 
            helpText: 'Comp paid for loans sourced by the Loan Officer directly (required)' 
        },
        {   label: 'Self Generated Refinance Transactions (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'Comp paid for Refinance loans sourced by the Loan Officer directly' 
        },
        {   label: 'Branch Generated Loan (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'Comp paid for loans where the lead was provided by the branch/corporate and/or accounts sourced or assigned by the branch or corporate (examples may be builders or real estate offices)' 

        },
        { label: 'Branch Generated Loan Refinance (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'Comp paid for Refinance loans where the lead was provided by the branch/corporate and/or accounts sourced or assigned by the branch or corporate (examples may be builders or real estate offices)' 

        },
        { label: 'Self Generated Consumer Paid Brokered Out Split (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'A traditional split on Borrower Paid Brokered out transactions only based on the gross revenue received by the company.  (example Borrower pays 2 points on a brokered transaction and has a 60% split, LO would receive 1.2%, again does not apply to Lender paid brokered out transactions.)' 

        },
        { label: 'Branch Generated Consumer Paid Brokered Out Split (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Branch Relationship Generated Consumer Paid Brokered Out Split (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Branch To Branch Referral (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'Comp paid for loans referred into the Rize Mortgage Specialty desk.  (common examples are out of state and bond loans that have SRP restrictions)' 

        },
        { label: 'Co-Originated Loan Split (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'A percentage Comp that a Loan Officer will receive if they originate a Loan with another LO (example LO 1 has a self gen comp of 150 bps with a Co-Origination split of 50% and LO 2 has a LO comp of 130 bps with a Co-Origination split of 60%.  LO 1 would receive 75 bps and LO 2 would receive 78 bps, this can be applied to all comp types)' 

        },
        { label: 'Self Generated Open Ended Credit (HELOC & Open Ended HECM) (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'A traditional split on Loans generated by the Loan Officer for all loans defined as open ended credit (example HELOC or open ended reverse mortgage)' 

        },
        { label: 'Company Generated Open Ended Credit (HELOC & Open Ended HECM) (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: 'A traditional split on Loans where the lead was provided by the branch/corporate and/or accounts sourced or assigned by the branch or corporate for all loans defined as open ended credit (example HELOC or open ended reverse mortgage)' 

        },
        { label: 'Branch Generated Relationship (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Branch Generated Relationship Refinance (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Speciality Desk Referral (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Transition Comp (bps)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
        { label: 'Branch Relationship Generated Open Ended Credit (HELOC & Open Ended HECM) (%)', 
            value: '', isBoxToggled: false, isMaxCompensation: false, 
            maxCompensation: '', isToggleHidden: false, 
            helpText: '' 

        },
    ],
    bonus: { _option: false, value: '' },
    upfront: { _option: false, value: '' },
    notes: '',
    reportingManager: '',
    filledBy: 'admin',
}


/** Cleared every time the modal is opened, so no message survives from last time. */
const initialError = {
    firstName: '',
    lastName: '',
    cellNumber: '',
    email: '',
    bonus: '',
    upfront: '',
};

const EmployeeInfoModal = ({ isOpen, onboardingForm, onSave, handleModalClose, employeeData=null, index=null, flowType='' }) => {
    const isRizeTeam = isRizeForRealtorFlow(flowType);
    const [data, setData] = useState(initialData);
    // Business Development positions are paid by Override (BPS), not salary
    const isOverrideOnly = isOverrideOnlyTitle(data.title);
    const [error, setError] = useState(initialError);

   useEffect(() => {
        if (!isOpen) return;
        const mergedData = (employeeData && Object.keys(employeeData).length > 0)
            ? { ...employeeData }
            : { ...initialData };
        // The default "Area Manager" is not one of the roles a Rize for Realtor team adds
        if (isRizeTeam && !RIZE_EMPLOYEE_TITLES.includes(mergedData.title)) {
            mergedData.title = RIZE_EMPLOYEE_TITLES[0];
        }
        setData(mergedData)
        setError(initialError);
   }, [isOpen, employeeData, isRizeTeam])

    const filteredArray = onboardingForm.filter((item) => item.Title === data.title);
    const employmentTypeOptions = filteredArray.length > 0
        ? JSON.parse(filteredArray[0].EmploymentType).map(option => ({ label: option, value: option }))
        : [];

    // A Rize for Realtor team only adds the Business Development roles and Loan Officers.
    // If none of them are in the dropdown data yet, fall back to the full list.
    const rizeTitleOptions = onboardingForm.filter((item) => RIZE_EMPLOYEE_TITLES.includes(item.Title));
    const titleOptions = (isRizeTeam && rizeTitleOptions.length > 0) ? rizeTitleOptions : onboardingForm;

    // Keep the shown/saved employment type inside the available options for the title
    const employmentTypeValue = employmentTypeOptions.some(option => option.value === data.employementType)
        ? data.employementType
        : (employmentTypeOptions[0]?.value || '');

    const handleSave = (e) => {
        console.log("error: ", error);
        
        e.preventDefault()
        const isFormValid = Object.values(error).every((err) => err === '');
        if (!isFormValid) {
            showValidationError("Please fill in valid data in all the fields.");
            return;
        }
        if (isOverrideOnly && !data.overrideBps) {
            showValidationError(`Please enter the Override (BPS) for the ${data.title}.`);
            return;
        }
        onSave({
            ...data,
            employementType: employmentTypeValue,
            // Business Development positions are paid by override, not salary
            overrideBps: isOverrideOnly ? data.overrideBps : '',
            compensationRate: isOverrideOnly ? '' : data.compensationRate,
        }, index);
        setData(initialData);
        handleModalClose();
    }


    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cellNumber') {
            const formattedPhoneNumber = formatPhoneNumber(value);
            setData({...data, [name]: formattedPhoneNumber});
            setError({...error, [name]: ''});
        }
        else if (name === 'email') {
            setData({...data, [name]: value})
            setError({...error, [name]: ''});
        }
        else if (name === 'compensationRate') {
            setData({ ...data, [name]: formatDollarWithComma(value)});
            setError({...error, [name]: ''});
        }
        else if (name === 'overrideBps') {
            // The API parses this as a number, so keep a single decimal point
            const [whole, ...decimals] = value.replace(/[^\d.]/g, '').split('.');
            setData({ ...data, [name]: decimals.length ? `${whole}.${decimals.join('')}` : whole });
            setError({...error, [name]: ''});
        }
        else {
            setData({...data, [name]: value});
            setError({...error, [name]: ''});
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (name === 'cellNumber') {
            // An untouched field is not invalid — `required` covers an empty one on
            // submit. Only a half typed number is flagged, and a complete one clears
            // the message again.
            const rawNumbers = value.replace(/[^\d]/g, '');
            setError({
                ...error,
                [name]: rawNumbers.length === 0 || rawNumbers.length === 10 ? '' : 'Please enter a valid phone number',
            });
        }
        else if (name === 'email') {
            var emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailPattern.test(value)) {
                setError({...error, [name]: 'Please enter a valid email address'});
            }
        }
        else if (name === 'firstName' || name === 'lastName') {
            // only the required text fields are checked here. The error map has to stay
            // keyed by field — setting it to a plain string used to break the
            // "is every error empty?" check on save, and optional fields (notes,
            // compensation rate, ...) must never block Submit.
            setError({...error, [name]: value === '' ? `Please enter a valid ${name}` : ''});
        }

      };

    

    const handleIsShowTransactions = () => {
        setData({...data, isShowTransactions: !data.isShowTransactions});
    }

    const handleIsOriginatingLoans = () => {
        setData({ ...data, isOriginatingLoans: !data.isOriginatingLoans });
    }

    const handleOptionFieldChange = (index, value) => {
        setData(currentData => ({
            ...currentData,
            transactions: currentData.transactions.map((item, i) => 
                                i === index 
                                ? {
                                    ...item, value: parseFloat(value)
                                } 
                                : item)
        }))
    }

    const handleOptionFieldToggle = (index) => {
        setData(currentData => ({
            ...currentData,
            transactions: currentData.transactions.map((item, i) => 
                                    i === index 
                                    ? {
                                        ...item, isBoxToggled: !item.isBoxToggled
                                    } 
                                    : item)
        }))
    }


    const handleMaxCompensationToggle = (index) => {  
        // if maxCompensation is not toggled, make input field empty              
        setData(currentData => ({
            ...currentData,
            transactions: currentData.transactions.map((item, i) => 
                                        i === index 
                                        ?   {
                                                ...item, 
                                                isMaxCompensation: !item.isMaxCompensation,
                                                maxCompensation: !item.isMaxCompensation ? item.maxCompensation : ''
                                            } 
                                        : item
                                )
        }))
    }

    const handleMaxCompensationFieldChange = (index, value) => {    
        setData(currentData => ({
            ...currentData,
            transactions: currentData.transactions.map((item, i) => 
                                        i === index 
                                        ?   {
                                                ...item, 
                                                maxCompensation: formatDollarWithComma(value)
                                            } 
                                        : item
                        )
        }))

    }


    const handleAdditionQuestionsChange = (e) => {
        const { name, value } = e.target;
        setData({...data, [name]: {...data[name], value: value}});
        setError({...error, [name]: ''});

    }

    const handleAdditionQuestionToggle = (name) => {
        setData({...data, [name]: {...data[name], _option: !data[name]._option}});
    }


    if (!isOpen) return null;

  
    return (
        <ModalShell
            title="Available for your New Hire"
            subtitle="Position, compensation and contact details"
            onClose={handleModalClose}
        >
            <form onSubmit={handleSave} className="flex-1 min-h-0 flex flex-col">
                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 py-6">
                    <div className="mx-auto max-w-3xl">
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <FloatingLabelDate
                                            label="Tentative Start Date"
                                            handleChange={handleChange}
                                            value={data.tentativeStartDate}
                                            name="tentativeStartDate"
                                            id="tentative_start_date_"
                                            required={true}
                                            min={earliestStartDate(data.tentativeStartDate)}
                                            />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <FloatingLabelInput
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            value={data.firstName}
                                            error={error.firstName}
                                            name="firstName"
                                            id="first_name_"
                                            label="New Employee First Name"
                                            required={true}
                                        />
                                </div>
                                <div className="sm:col-span-3">
                                    <FloatingLabelInput
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            value={data.lastName}
                                            error={error.lastName}
                                            name="lastName"
                                            id="last_name_"
                                            label="New Employee Last Name"
                                            required={true}
                                        />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <FloatingLabelInput
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            value={data.cellNumber}
                                            error={error.cellNumber}
                                            name="cellNumber"
                                            id="cell_number_"
                                            type="tel"
                                            label="New Employee Cell Number"
                                            required={true}
                                        />
                                </div>
                                <div className="sm:col-span-3">
                                    <FloatingLabelInput
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            value={data.email}
                                            error={error.email}
                                            name="email"
                                            id="email_"
                                            type="email"
                                            label="New Employee Personal Email Address"
                                            required={true}
                                        />
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 mb-8 pt-8 border-t border-slate-200">
                            <h3 className="text-lg font-semibold text-[#0a1445]">
                                Position and Compensation Information
                            </h3>
                        </div>

                        <div className="space-y-8">
                            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <FloatingLabelSelect
                                            handleChange={handleChange}
                                            value={data.title}
                                            error={error.title}
                                            name="title"
                                            id="title_"
                                            label="New Employee Position / Title"
                                            options={titleOptions}
                                            required={true}
                                        />
                                </div>

                                <div className="sm:col-span-3">
                                    <FloatingLabelSelect
                                            handleChange={handleChange}
                                            value={employmentTypeValue}
                                            error={error.employementType}
                                            name="employementType"
                                            id="employement_type_"
                                            label="Employment Type"
                                            options={employmentTypeOptions}
                                            required={true}
                                        />
                                </div>
                            </div>
                            {/* Override / Bonus based compensation — Business Development positions */}
                            {isOverrideOnly && (
                                <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-3">
                                        <FloatingLabelInput
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            value={data.overrideBps}
                                            error={error.overrideBps}
                                            name="overrideBps"
                                            id="override_bps_"
                                            type="text"
                                            label="Override (BPS)"
                                            required={true}
                                        />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <p className="pt-2 text-sm text-gray-500">
                                            Compensation for this position is override / bonus based — no salary.
                                        </p>
                                    </div>
                                </div>
                            )}
                            { !isOverrideOnly && filteredArray.length > 0 && filteredArray[0].CompensationRateBox === "true" && (
                                <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-3">
                                            <FloatingLabelInput
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                value={data.compensationRate}
                                                name="compensationRate"
                                                id="compensation_rate_"
                                                type="text"
                                                label="Compensation Rate ($)"
                                                //required={true}
                                                isCurrency={true}
                                            />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <p className="pt-2 text-sm text-gray-500">
                                            { employmentTypeOptions.length > 0 &&
                                                (employmentTypeOptions[0].value.toLowerCase().includes("hourly") ?
                                                    "/hour"
                                                : "/year"
                                                )
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/*
                            Will this employee be originating loans? (yes or no toggle)
                            Right above Does this position offer LO Comp?
                            If no, Does this poision offer LO Comp will be no and hidden.
                            NMLS number becomes mandatory in the wizzard
                        */}
                        <div className="mt-10 space-y-3">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <ToggleButton
                                    label="Will this employee be originating loans?"
                                    isToggled={data.isOriginatingLoans}
                                    handleToggle={handleIsOriginatingLoans}
                                    name="originationLoans_"
                                />
                            </div>

                            {/* LO Comp */}
                            {filteredArray.length > 0 && filteredArray[0].ShowCommisionToggle && data.isOriginatingLoans &&  (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                    <ToggleButton
                                        label="Does this position offer LO Comp?"
                                        isToggled={data.isShowTransactions}
                                        handleToggle={handleIsShowTransactions}
                                        name="showTransactions_"
                                    />
                                </div>
                            )}
                        </div>

                        { data.isShowTransactions && (
                            <div className="mt-10 mb-6 pt-8 border-t border-slate-200">
                                <h3 className="text-lg font-semibold text-[#0a1445]">
                                    Please Select the Commission Compensation Buckets You Would Like to Have Available to Your New Hire
                                </h3>
                            </div>
                        )}
                        {data.isShowTransactions && (
                            <div className="space-y-4">
                                {data.transactions.map((dataObject, index) => (
                                    <div key={index} className="rounded-xl border border-slate-200 px-4 py-3">
                                        <OptionalFields
                                            handleOptionFieldChange={(e) => handleOptionFieldChange(index, e.target.value)}
                                            handleOptionFieldToggle={() => handleOptionFieldToggle(index)}
                                            handleMaxCompensationToggle={() => handleMaxCompensationToggle(index)}
                                            handleMaxCompensationFieldChange={(e) => handleMaxCompensationFieldChange(index, e.target.value)}
                                            handleBlur={handleBlur}
                                            data={dataObject}
                                            error={error}
                                            label={dataObject.label}
                                            name={ dataObject.label.replace(/\s/g, '')}
                                            />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Additional Questions */}
                        <div className="mt-10 space-y-3">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <ToggleButton
                                    label="Is there any type of override or bonus?"
                                    isToggled={data.bonus._option}
                                    handleToggle={() => handleAdditionQuestionToggle('bonus')}
                                    name={'bonus__'}
                                />
                                {data.bonus._option && (
                                    <div className="mt-4">
                                        <FloatingLabelTextArea
                                            handleChange={handleAdditionQuestionsChange}
                                            handleBlur={handleBlur}
                                            value={data.bonus.value}
                                            error={error.bonus}
                                            name={'bonus'}
                                            id='bonus_'
                                            label=""
                                            type='text'
                                            placeholder='Describe override or bonus'
                                            rows="3"
                                            required={true}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <ToggleButton
                                    label="Is there any type of sign-on bonus or upfront incentive?"
                                    isToggled={data.upfront._option}
                                    handleToggle={() => handleAdditionQuestionToggle('upfront')}
                                    name={'upfront__'}
                                />
                                {data.upfront._option && (
                                    <div className="mt-4">
                                        <FloatingLabelTextArea
                                            handleChange={handleAdditionQuestionsChange}
                                            handleBlur={handleBlur}
                                            value={data.upfront.value}
                                            error={error.upfront}
                                            name={'upfront'}
                                            id={'upfront_'}
                                            label=""
                                            type='text'
                                            placeholder='Describe Sign-on or upfront incentive terms'
                                            rows='3'
                                            required={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Text Area */}
                        <div className="mt-10">
                            <label htmlFor="notes_" className="font-medium text-slate-800">
                                Compensation and Other Notes regarding the new hire
                            </label>

                            <div className="border border-slate-200 rounded-md mt-3 overflow-hidden">
                                <textarea
                                    id="notes_"
                                    value={data.notes}
                                    name="notes"
                                    rows="6"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    // required={true}
                                    placeholder='(ie sliding volume scale, yearly volume incentive, etc.)'
                                    className={`w-full p-4 border-none outline-none ring-0 focus:ring-0 ${data.notes  ? 'bg-white' : 'bg-gray-50'}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sticky footer */}
                <div className="shrink-0 border-t border-slate-200 bg-white px-5 sm:px-8 py-4">
                    <div className="mx-auto max-w-3xl flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                        <button
                            className="text-custom-purple border-2 border-custom-purple font-semibold px-4 py-2 rounded-full flex items-center justify-center min-h-12 sm:w-40 w-full"
                            type='button'
                            onClick={handleModalClose}
                            >
                                Cancel
                        </button>
                        <button
                            className="bg-custom-purple text-white font-semibold px-4 py-2 rounded-full flex items-center justify-center min-h-12 sm:w-40 w-full"
                            type='submit'
                            >
                                { employeeData && Object.keys(employeeData).length > 0 ? 'Save Changes' : 'Submit'}
                        </button>
                    </div>
                </div>
            </form>
        </ModalShell>
    );
  };

export default EmployeeInfoModal;